import React, { useState } from 'react'
import styled from 'styled-components'
import SearchButton from 'components/form/SearchButton'
import { useDispatch } from 'react-redux'
import { updateCourseProviderVersion, updateCourseProviderVersionMap } from 'store/course/thunks'
import { useSelector } from 'react-redux'
import { selectCourse } from 'store/course/slice'
import LoadingSpinnerGreen from 'components/ui/LoadingSpinnerGreen'
import { Grid, Tooltip } from '@mui/material'
import { FormattedMessageWrapper } from 'components/ui/FormattedMessageWrapper'
import InfoIcon from '@mui/icons-material/Info'
import { Checkbox } from '@mui/material'
import { COURSE_PROVIDER } from 'config'

interface Props {
  course: Course
}

export default function CourseProviderVersionUpdate({ course }: Props) {
  const dispatch = useDispatch()
  const { updateCourseProviderVersionStatus, updateCourseProviderVersionMapStatus } = useSelector(selectCourse)
  const [forceChecked, setForceChecked] = useState(false)
  const invalidationDate = new Date('1999-12-02 00:00:00')

  const isDisabled = () => {
    if (course.id > 0 && course.provider?.abbreviation !== COURSE_PROVIDER.GGB) {
      return true
    }
    return false
  }

  const handleCourseVersionUpdate = () => {
    const courseVersionId = course.versions?.find((v) => v.isOfficial)?.id
    if (!courseVersionId || !course.providerCourseVersionMap?.id || !course.provider?.abbreviation) {
      return
    }
    if (forceChecked) {
      const providerVersionId = course.providerCourseVersionMap.providerVersionId
      const payload: CourseProviderVersionMapUpdatePayload = {
        courseId: course.id,
        courseVersionId: courseVersionId,
        courseProviderVersionId: course.providerCourseVersionMap?.id,
        dateFrom: invalidationDate,
        dateTo: invalidationDate,
        newProviderVersionId: `force-${new Date().valueOf()}-${providerVersionId}`,
        providerAbbreviation: course.provider?.abbreviation,
      }
      dispatch(updateCourseProviderVersionMap(payload))
      return
    }
    if (!course.provider || !course.providerCourseMap) {
      return
    }
    /**
     * Due to Cores provider object courseId and courseVersionId are resembling
     * provider_course_id - in course_provider_map and provider_version_id - in course_provider_version_map table.
     * Do not confuse these with our internal id's in any case. These are named according to cores provider object.
     */
    const payload: CourseProviderVersionUpdatePayload = {
      providerId: course.provider?.id,
      courseId: course.providerCourseMap?.providerCourseId,
      courseVersionId: course.providerCourseVersionMap.providerVersionId,
      action: 'update',
      jurisdiction: course.provider.abbreviation,
      id: course.id,
    }
    dispatch(updateCourseProviderVersion(payload))
  }

  if (updateCourseProviderVersionStatus === 'loading' || updateCourseProviderVersionMapStatus === 'loading') {
    return (
      <LoaderContainer>
        <LoadingSpinnerGreen size={92} />
      </LoaderContainer>
    )
  }

  const renderTooltipContent = () => {
    return (
      <TooltipContent>
        <FormattedMessageWrapper id="strings.courseProviderUpdateTooltip" />
      </TooltipContent>
    )
  }

  const renderTooltip = () => {
    return (
      <Tooltip sx={{ color: '#1AA85D' }} title={renderTooltipContent()} placement="bottom-start">
        <InfoIcon />
      </Tooltip>
    )
  }

  return (
    <TransferForm>
      <UpdateButton
        status={'idle'}
        text={!forceChecked ? 'Update' : 'Force Update'}
        onClick={handleCourseVersionUpdate}
        disabled={!isDisabled()}
      />
      <Grid sx={{ display: 'flex', alignItems: 'center', paddingX: '5px' }}>
        <Tooltip title="Force Update">
          <Checkbox checked={forceChecked} onChange={(e) => setForceChecked(e.target.checked)} />
        </Tooltip>
        {renderTooltip()}
      </Grid>
    </TransferForm>
  )
}

const UpdateButton = styled(SearchButton)`
  &.Mui-disabled {
    background-color: ${(props) => props.theme.palette.primary.main};
    color: ${(props) => props.theme.customPalette.light};
    opacity: 0.5;
  }
  text-transform: none;
`
const TransferForm = styled.div`
  display: flex;
  margin-top: 10px;
  justify-self: flex-start;
  align-self: flex-start;
  & > span {
    color: ${(props) => props.theme.palette.primary.main};
    width: 100%;
  }
  & > input {
    height: 30px;
    margin-top: 10px;
    padding: 0 10px;

    &::placeholder {
      color: ${(props) => props.theme.customPalette.mediumGray};
    }
  }
  & > button {
    background-color: ${(props) => props.theme.palette.primary.main};
    color: ${(props) => props.theme.customPalette.light};
    border: 1px solid;
    border-color: ${(props) => props.theme.palette.primary.main};
    border-radius: 5px;
    cursor: pointer;
    &:not(.disabled):hover {
      opacity: 0.85;
    }

    &.disabled {
      background-color: ${(props) => props.theme.customPalette.mediumGray};
      border-color: ${(props) => props.theme.customPalette.mediumGray};
      cursor: unset;
    }
  }
`

const LoaderContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 50px 0;
`

const TooltipContent = styled.div`
  & > * {
    font-style: normal;
    font-size: 14px;
    line-height: 150%;
    color: #404040;
  }
  & > h5 {
    font-weight: 500;
  }
  & > p {
    font-weight: 400;
    margin-bottom: 10px;
  }
`
